import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/useAuth';
import api from '@/services/axiosConfig';
import { formatLocalDate } from '@/utils/formatLocalDate';
import { QUIZ_TYPE_PROFICIENCY_TEST } from '@/constants';

interface Exam {
  exam_id: number;
  title: string;
  exam_type: number;
  total_point: number;
  exam_start_datetime: string;
  exam_end_datetime: string;
  exam_status: boolean;
}

const isTestAvailable = (exam: Exam): boolean => {
  const now = new Date();
  const start = new Date(formatLocalDate(exam.exam_start_datetime));
  const end = new Date(formatLocalDate(exam.exam_end_datetime));

  return !exam.exam_status && now >= start && now <= end;
};

export const useTestCount = () => {
  const { userId } = useAuth();

  const { data: miniTests = [] } = useQuery<Exam[], Error>({
    queryKey: ['miniTests', userId],
    queryFn: async () => {
      const { data } = await api.get(`/quiz/exams/${userId}/assigned-quizzes`);
      return data;
    },
    staleTime: 0,
  });

  const { data: proficiencyTests = [] } = useQuery<Exam[], Error>({
    queryKey: ['proficiencyTests', userId],
    queryFn: async () => {
      const { data } = await api.get(`/quiz/exams/${userId}/proficiency_test`);
      return data;
    },
    staleTime: 0,
  });
  const availableMiniTests = miniTests.filter(isTestAvailable);
  const availableProficiencyTests = proficiencyTests.filter(isTestAvailable);

  const allMiniTests = miniTests.length > 0;
  const allProficiencyTests = proficiencyTests.length > 0;

  const totalTestCount =
    availableMiniTests.length + availableProficiencyTests.length;

  const hasProficiencyTest = availableProficiencyTests.some(
    (test) => test.exam_type === QUIZ_TYPE_PROFICIENCY_TEST,
  );

  return {
    totalTestCount,
    miniTests: availableMiniTests,
    proficiencyTests: availableProficiencyTests,
    hasProficiencyTest,
    allMiniTests,
    allProficiencyTests,
  };
};
